import { useState, useEffect, type FormEvent } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { PromptTypes } from '@kinde/js-utils';
import { Shield, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { useGuestSignupModal } from '../context/GuestSignupModalContext';
import { deriveDeepLinkPath } from '../utils/deriveDeepLinkPath';
import {
  useSignupContext,
  ensureAnonSessionId,
  type SignupScreen2,
} from '../hooks/useSignupContext';
import { useUnauthAttribution } from '../hooks/useUnauthAttribution';
import { useUnauthRumAction } from '../hooks/useUnauthRumAction';
import { AccountCreationModalScreen2 } from './AccountCreationModalScreen2';

/**
 * Account creation modal — two-screen flow per spec Item 3.
 *
 * Screen 1: email + terms checkbox. Always shown.
 *           (Q1 decision: firstName/lastName removed because Kinde doesn't
 *           pre-fill those fields and asking twice is bad UX. Kinde collects
 *           them on its hosted page; AuthCallback reads them from the JWT.)
 * Screen 2: FP identification (Yes/No → dependent dropdowns). Skipped when
 *           a referral resolution (Item 5) already identified the user's FP
 *           or partner company (`referralType` ∈ {FP, PARTNER}).
 *
 * Fallback path (Kinde forces email verification):
 *   1. User submits Screen 1 → advance to Screen 2 (or skip)
 *   2. User submits Screen 2 → persist full context to localStorage
 *   3. Hand off to Kinde with prompt=create + email pre-filled
 *   4. AuthCallback applies signupAttribution + Screen 2 data after Kinde returns
 */

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

type Step = 'screen1' | 'screen2';

export const AccountCreationModal = () => {
  const { state, closeModal } = useGuestSignupModal();
  const { save: saveSignupContext, clear: clearSignupContext } = useSignupContext();
  const { attribution } = useUnauthAttribution();
  const { login: kindeLogin, isLoading: kindeLoading } = useKindeAuth();
  const trackAction = useUnauthRumAction();

  const [step, setStep] = useState<Step>('screen1');
  const [email, setEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Field-tagged error so we can wire aria-invalid/aria-describedby per input. */
  const [error, setError] = useState<{ field: 'email' | 'terms' | 'form'; message: string } | null>(null);

  // Screen 2 is skipped when the visitor's FP is already known:
  // - FP referral code (Item 5)
  // - Partner-company referral code (Item 5)
  // - Email invite from a Financial Advisor (Item 6 enhancement — confirmed by Brooks).
  //   Parity with the referral-code skip; spec was silent on this case but the logic
  //   is the same since handleInviteAcceptance creates the FA↔careseeker relation
  //   regardless of the Screen 2 answer.
  const shouldSkipScreen2 =
    (state.isOpen && state.skipAdvisorStep === true) ||
    attribution?.referralType === 'FP' ||
    attribution?.referralType === 'PARTNER' ||
    attribution?.inviterRole === 'FINANCIAL_ADVISOR';

  // Reset form whenever the modal opens fresh. Pre-fill email from email-invite
  // cache (Item 6) when present — saves the invitee from retyping.
  const prefilledEmail = state.isOpen ? (state.email ?? attribution?.inviteEmail ?? '') : '';
  useEffect(() => {
    if (state.isOpen) {
      setStep('screen1');
      setEmail(prefilledEmail);
      setTermsAccepted(false);
      setError(null);
      setSubmitting(false);
    }
  }, [state.isOpen, prefilledEmail]);

  const proceedToKinde = async (screen2?: SignupScreen2) => {
    if (!state.isOpen) return;
    setSubmitting(true);
    try {
      const anonSessionId = ensureAnonSessionId();
      const deepLinkPath = deriveDeepLinkPath(
        state.trigger,
        state.triggerEntityId,
        state.providerType,
      );
      const normalizedEmail = email.trim().toLowerCase();

      // Carry a guest-built navigator session id (if any) across the Kinde redirect
      // so AuthCallback can claim the orphaned care plan for the new account.
      // Captured here (not per call-site) so any signup started mid-navigator flow
      // carries it. bquest_nav_session_id = completed plan; chat_session_id = in-progress.
      const navigatorSessionId =
        sessionStorage.getItem('bquest_nav_session_id') ??
        localStorage.getItem('chat_session_id') ??
        undefined;

      saveSignupContext({
        email: normalizedEmail,
        termsAcceptedAt: new Date().toISOString(),
        trigger: state.trigger,
        triggerEntityId: state.triggerEntityId,
        anonSessionId,
        deepLinkPath,
        signupReferralCode: attribution?.referralCode,
        screen2,
        inviteId: attribution?.inviteId,
        ...(navigatorSessionId ? { navigatorSessionId } : {}),
      });

      await kindeLogin({
        loginHint: normalizedEmail,
        prompt: PromptTypes.create,
      });
      // kindeLogin redirects the browser; nothing after this runs.
    } catch (err) {
      // kindeLogin failed (popup blocked / network / SDK) — the browser did NOT
      // redirect, so the context we just persisted is stale. Clear it so a later
      // unrelated login isn't mis-attributed as this signup (unauth.signup_completed).
      clearSignupContext();
      setSubmitting(false);
      setError({
        field: 'form',
        message: err instanceof Error ? err.message : 'Could not start signup. Please try again.',
      });
    }
  };

  const handleScreen1Submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!isValidEmail(trimmedEmail)) {
      setError({ field: 'email', message: 'Please enter a valid email address.' });
      return;
    }
    if (!termsAccepted) {
      setError({ field: 'terms', message: 'You must accept the terms to continue.' });
      return;
    }
    setError(null);

    if (state.isOpen) {
      trackAction('unauth.signup_modal_screen1_submit', {
        trigger: state.trigger,
        triggerEntityId: state.triggerEntityId,
      });
    }

    if (shouldSkipScreen2) {
      await proceedToKinde();
      return;
    }
    setStep('screen2');
  };

  return (
    <Dialog open={state.isOpen} onOpenChange={(open) => !open && !submitting && closeModal()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="p-2 rounded-full bg-[#026857]/10">
              <Shield className="w-5 h-5 text-[#026857]" />
            </div>
          </div>
          <DialogTitle className="text-xl">
            {step === 'screen1' ? 'Access your bQuest Account' : 'About your financial advisor'}
          </DialogTitle>
          <DialogDescription>
            {step === 'screen1'
              ? "We'll send a verification code to this email. There is no cost to create an account or access your available bQuest resources. Your information stays private. We'll never share or sell your information to a third party"
              : 'Optional — this helps us connect your care guide with your financial advisor.'}
          </DialogDescription>
        </DialogHeader>

        {/* key={step} remounts on step change so tailwindcss-animate's animate-in
            plays the slide+fade each time (same lib the Dialog uses). Mirrors the
            auth-page step transition without the dark-bg AuthStepPanel chrome. */}
        <div key={step} className="animate-in fade-in-0 slide-in-from-right-4 duration-300 motion-reduce:animate-none">
        {step === 'screen1' ? (
          <form onSubmit={handleScreen1Submit} className="space-y-4">
            <div>
              <Label htmlFor="signup-email">Email *</Label>
              <Input
                id="signup-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                maxLength={254}
                disabled={submitting}
                required
                aria-invalid={error?.field === 'email'}
                aria-describedby={
                  error?.field === 'email' ? 'signup-email-error' : 'signup-email-hint'
                }
              />
              {error?.field === 'email' && (
                <p id="signup-email-error" className="mt-1.5 text-xs text-red-600" role="alert">
                  {error.message}
                </p>
              )}
              <p id="signup-email-hint" className="mt-1.5 text-xs text-gray-500">
                Create your account to unlock personalized care resources, saved providers,
                educational content, and your dashboard.
              </p>
            </div>

            <div>
              <div className="flex items-start gap-2 pt-1">
                <Checkbox
                  id="signup-terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  disabled={submitting}
                  className="mt-0.5"
                  aria-invalid={error?.field === 'terms'}
                  aria-describedby={error?.field === 'terms' ? 'signup-terms-error' : undefined}
                />
                <label htmlFor="signup-terms" className="text-sm text-gray-700 leading-5 cursor-pointer">
                  I agree to the{' '}
                  <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="underline text-[#026857]">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline text-[#026857]">
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
              {error?.field === 'terms' && (
                <p id="signup-terms-error" className="mt-1.5 text-xs text-red-600" role="alert">
                  {error.message}
                </p>
              )}
            </div>

            {error?.field === 'form' && (
              <p className="text-sm text-red-600" role="alert">{error.message}</p>
            )}

            <Button
              type="submit"
              disabled={submitting || kindeLoading}
              className="w-full bg-[#026857] hover:bg-[#025246] text-white font-bold"
            >
              {submitting || kindeLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {shouldSkipScreen2 ? 'Redirecting…' : 'Continuing…'}
                </>
              ) : shouldSkipScreen2 ? (
                'Create account'
              ) : (
                'Continue'
              )}
            </Button>

            {shouldSkipScreen2 && (
              <p className="text-center text-xs text-gray-500">
                We'll send you a verification email to finish setting up your account.
              </p>
            )}
          </form>
        ) : (
          <AccountCreationModalScreen2
            onSubmit={(data) => { void proceedToKinde(data); }}
            onBack={() => setStep('screen1')}
            submitting={submitting || kindeLoading}
          />
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
